/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled/macro'; // for dom selector
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from 'component/common/block/text-block';
import { formatDate, removeEmailDomain, removePrefix } from '@util/string';
import IconBlock from 'component/common/block/icon-block';
import { useModifyShelfMutation, useModifySubscriptionMutation } from '@hook/moashelf/useApi';
import { useTypedDispatch } from '@hook/common/useStore';
import Line from '@component/common/line';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { v4 as uuid4 } from 'uuid';
import Gap from '@component/common/gap';
import BoxBlock from '@component/common/block/box-block';
import Spacer from '@component/common/spacer';

interface IProps {
  subscriptionKey: string;
  name: string;
  email: string;
  description: string;
  publishDays: string[];
  isBookmark: boolean;
  trashedAt: string;
}

// WebShelfItemContainer와 똑같이
const WebSubscriptionItemContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  margin: 0 auto 0 auto;
  width: 100%;
  height: 50px;
  padding: 0 ${size.MARGIN_2} 0 ${size.MARGIN_2};
`;

const MobileProviderItemContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: column;
  /* justify-content: flex-start; */
  justify-content: center;
  align-items: flex-start;
  /* width: calc(100% - 2 * ${size.MARGIN_2}); */
  width: 100%;
  height: 120px;
  padding: ${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1};
`;

const PublishDaysContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 5px;
`;

const WebDeleteContainer = styled.div`
  display: none;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`;

const MobileDeleteContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div`
  cursor: pointer;

  &:hover {
    box-shadow: ${IS_IYAGIGO_MOBILE_VIEW ? 'none' : color.SURFACE_SHADOW};
  }

  &:hover ${WebDeleteContainer} {
    display: flex;
  }

  margin: 0 auto 0 auto;
`;

// const MOBILE_TEXT_DESIGN = {
//   text: { size: 'medium', bold: true, color: color.BLACK_2 },
//   box: { alignX: 'left' },
// } as const;

const TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2 },
  box: { alignX: 'left' },
} as const;

const ICON_DESIGN = {
  icon: { size: 'medium', color: color.GRAY_2, kind: 'outlined' },
  box: { width: '30px' },
} as const;

const FirstRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-self: center;
  width: 100%;
`;

const SubscriptionItem: React.FC<IProps> = ({
  subscriptionKey,
  name,
  email,
  description,
  publishDays,
  isBookmark,
  trashedAt,
}) => {
  const dispatch = useTypedDispatch();
  const modifySubscriptionMutation = useModifySubscriptionMutation();

  const handleBookmarkClick = async (ev) => {
    ev.stopPropagation();
    await modifySubscriptionMutation.mutateAsync({ subscriptionKey, isBookmark: !isBookmark });
  };

  const handleTrashClick = async (ev) => {
    ev.stopPropagation();
    await modifySubscriptionMutation.mutateAsync({ subscriptionKey, isTrash: true });
  };

  const handleUntrashClick = async (ev) => {
    ev.stopPropagation();
    await modifySubscriptionMutation.mutateAsync({ subscriptionKey, isTrash: false });
  };

  const days = ['월', '화', '수', '목', '금', '토', '일'];
  const PublishDays = days.map((day) => {
    const isPublishDay = publishDays.includes(day);
    return (
      <TextBlock
        key={uuid4()}
        text={day}
        design={{
          text: { size: 'tiny', color: color.BLACK_2 },
          box: {
            width: '20px',
            height: '20px',
            backgroundColor: isPublishDay ? color.YELLOW_1 : color.GRAY_3,
            borderRadius: '20px',
          },
        }}
      />
    );
  });

  return (
    <Wrapper>
      {IS_IYAGIGO_MOBILE_VIEW ? (
        <MobileProviderItemContainer>
          <FirstRowContainer>
            <TextBlock text={name} design={{ text: { ...TEXT_DESIGN.text, size: 'medium', bold: true } }} />
            <MobileDeleteContainer>
              {trashedAt ? (
                <IconBlock icon='untrash' design={ICON_DESIGN} click={{ handler: handleUntrashClick }} />
              ) : (
                <IconBlock icon='trash' design={ICON_DESIGN} click={{ handler: handleTrashClick }} />
              )}
            </MobileDeleteContainer>
          </FirstRowContainer>
          <TextBlock
            text={`<${email}>`}
            design={{
              box: TEXT_DESIGN.box,
              text: { ...TEXT_DESIGN.text, size: 'tiny', bold: false, color: color.GRAY_1 },
            }}
          />
          <Gap height='5px' />
          <PublishDaysContainer>{PublishDays}</PublishDaysContainer>
          <Gap height='5px' />
          <TextBlock
            text={description}
            design={{ text: { ...TEXT_DESIGN.text, multiline: 2 }, box: { alignX: 'left' } }}
          />
        </MobileProviderItemContainer>
      ) : (
        <WebSubscriptionItemContainer>
          <TextBlock
            text={name}
            design={{
              box: { ...TEXT_DESIGN.box, width: '120px' },
              text: { ...TEXT_DESIGN.text, oneline: true },
            }}
          />
          {/*<IconBlock*/}
          {/*  icon='bookmark'*/}
          {/*  design={{*/}
          {/*    box: { ...ICON_DESIGN.box, margin: `0 ${size.MARGIN_1} 0 ${size.MARGIN_1}` },*/}
          {/*    icon: { ...ICON_DESIGN.icon, checkedColor: color.YELLOW_1, checked: isBookmark },*/}
          {/*  }}*/}
          {/*  onClick={handleBookmarkClick}*/}
          {/*/>*/}
          <Gap width={size.MARGIN_1} />
          <TextBlock
            text={removePrefix(description, '[Catch] ')}
            design={{
              box: { ...TEXT_DESIGN.box, width: 'calc(100% - 350px)' },
              text: { ...TEXT_DESIGN.text, oneline: true },
            }}
          />
          <WebDeleteContainer>
            {trashedAt ? (
              <IconBlock icon='untrash' design={ICON_DESIGN} click={{ handler: handleUntrashClick }} />
            ) : (
              <IconBlock icon='trash' design={ICON_DESIGN} click={{ handler: handleTrashClick }} />
            )}
          </WebDeleteContainer>
          <Spacer />
          <PublishDaysContainer>{PublishDays}</PublishDaysContainer>
        </WebSubscriptionItemContainer>
      )}
      <Line width='100%' color={color.GRAY_3} />
    </Wrapper>
  );
};

export default SubscriptionItem;
